import classNames from 'clsx';
import PropTypes from 'prop-types';
import { useContext, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { renderRoutes } from 'react-router-config';
import { useAuthEffects } from 'src/common/hooks/useAuthEffects';
import { DISPLAY_ADS_LIBRARY_ENABLED } from '../constants/experiments/ads';
import useBraze from '../hooks/useBraze';
import useExperiment from '../hooks/useExperiment';
import useOneTrustButton from '../hooks/useOneTrustButton';
import useSideBar from '../hooks/useSideBar';
import { LocationAndLocalizationContext } from '../providers/LocationAndLocalizationProvider';
import { useRouteProps } from '../providers/RoutePropsProvider';
import {
  selectBottomAdBannerHeight,
  selectIsDesktop,
  selectIsDiscord,
  selectIsMobile,
  selectTopBannerHeight,
} from '../selectors/app';
import {
  selectIsSubscriptionEnabled,
  selectSubscriptionSuccessPartnerConfig,
} from '../selectors/config';
import { selectShouldShowDesktopUpdateBanner } from '../selectors/desktop';
import { selectAuthViewType } from '../selectors/loginDetails';
import { useDetermineAdEligibility } from '../utils/ads/determineAdEligibility';
import determineSponsorshipEnabled from '../utils/ads/determineSponsorshipEnabled';
import getTopBannerOffsetStyles from '../utils/banner/getTopBannerOffsetStyles';
import hideHeaderHelper from '../utils/hideHeaderHelper';
import { useShouldShowPlayer } from '../utils/playerStatus/shouldShowPlayer';
import { useShowAppToastInPlayer } from '../utils/showAppToastInPlayer';
import BottomBanner from './ads/BottomBanner';
import WithAds from './ads/WithAdsLoader';
import appCss from './app.module.scss';
import { InfoBanner } from './banner/InfoBanner';
import InstallDesktopUpdateBanner from './desktop/InstallDesktopUpdateBanner';
import Footer from './footer/Footer';
import GlobalDialogs from './globalDialogs/GlobalDialogsLoader';
import { BrowsiesHeader } from './header/BrowsiesHeader';
import Header from './header/Header';
import Loader from './Loader';
import MessagePopover from './messagePopover/MessagePopover';
import MiniPlayer from './player/MiniPlayerLoader';
import ScrollReset from './shared/ScrollReset';
import LeftSideLoader from './sidebar/LeftSideLoader';
import RightSide from './sidebar/RightSide';

function getContentStyles(routeProps, topBannerHeight, bottomBannerHeight) {
  const contentStyles = {};

  if (routeProps.bgColor) {
    contentStyles.backgroundColor = routeProps.bgColor;
  }
  if (bottomBannerHeight) {
    contentStyles.paddingBottom = bottomBannerHeight;
  }

  if (topBannerHeight) {
    Object.assign(contentStyles, getTopBannerOffsetStyles(topBannerHeight));
  }

  return contentStyles;
}

export default function AppRoot({ route, history }) {
  const routeProps = useRouteProps();
  const guideId = routeProps.guideContext?.guideId;
  const isMobile = useSelector(selectIsMobile);
  const isDesktop = useSelector(selectIsDesktop);
  const isDiscord = useSelector(selectIsDiscord);
  const isPlayerOpen = useShouldShowPlayer();
  const shouldReserveMiniPlayerSpace = isPlayerOpen && !routeProps.disablePlayer;
  const isSubscriptionEnabled = useSelector(selectIsSubscriptionEnabled);
  const isDisplayAdsLibraryEnabled = useExperiment(DISPLAY_ADS_LIBRARY_ENABLED, false);
  const shouldShowDesktopUpdateBanner = useSelector(selectShouldShowDesktopUpdateBanner);
  const authViewType = useSelector(selectAuthViewType);
  const topBannerHeight = useSelector(selectTopBannerHeight);
  const canShowAds = useDetermineAdEligibility(guideId);
  const sponsorshipEnabled = useSelector(determineSponsorshipEnabled(guideId));
  const bottomAdBannerHeight = useSelector(selectBottomAdBannerHeight);
  const {
    getLocalizedText,
    locale,
    location: { pathname, search, query },
  } = useContext(LocationAndLocalizationContext);
  const contentScrollerRef = useRef();
  const appContentRef = useRef();
  const {
    hasRightSide,
    isRightSideOpen,
    isLeftSideDocked,
    isLeftSideOpen,
    hideLeftSide,
    handleLeftSideToggle,
  } = useSideBar(isMobile, query, pathname);
  const { showFloatingGdprButton } = useOneTrustButton({ isLeftSideDocked });
  const showAppToast = !useShowAppToastInPlayer() && !routeProps.isMapViewRoute;
  const subscriptionSuccessPartnerConfig = useSelector(selectSubscriptionSuccessPartnerConfig);
  const contentStyles = getContentStyles(routeProps, topBannerHeight, bottomAdBannerHeight);
  const shouldHideHeader = useMemo(
    () =>
      hideHeaderHelper(
        query,
        pathname,
        authViewType,
        isMobile,
        routeProps,
        subscriptionSuccessPartnerConfig,
      ),
    [query, pathname, authViewType, isMobile, routeProps, subscriptionSuccessPartnerConfig],
  );
  // Not gated on docking: the header is hidden from the large breakpoint up, where the left side
  // docks, and the server can't tell which side of it the viewport is.
  const showSidebarToggle = !hideLeftSide;
  const sidePadding = {
    [appCss.rightSideOpen]: hasRightSide,
    [appCss.leftSideOpen]: !hideLeftSide ? isLeftSideOpen : false,
    [appCss.homeLeftSideOpen]: routeProps.isLanding,
  };
  // The same element between renders lets React skip the page when only the app chrome changed.
  // Re-rendering the routes would also hand every router link on the page a new context value.
  const content = useMemo(
    () =>
      renderRoutes(route.routes, {
        history,
        canShowAds,
        isLeftSideDocked,
        isDiscord,
        sponsorshipEnabled,
        isRightSideOpen,
      }),
    [
      route.routes,
      history,
      canShowAds,
      isLeftSideDocked,
      isDiscord,
      sponsorshipEnabled,
      isRightSideOpen,
    ],
  );
  const isMinimalFooter = isDiscord || isDesktop;
  const minimalHeaderTitle = routeProps.minimalHeaderTitleKey
    ? getLocalizedText(routeProps.minimalHeaderTitleKey)
    : undefined;

  useAuthEffects();
  useBraze();

  return (
    <>
      {!routeProps.disablePlayer && <MiniPlayer />}
      {!routeProps.hideAds && !isDisplayAdsLibraryEnabled && <WithAds locale={locale} />}
      <InfoBanner />
      {shouldShowDesktopUpdateBanner && <InstallDesktopUpdateBanner />}
      {routeProps.showBrowsiesHeader && (
        <BrowsiesHeader
          guideId={guideId}
          contentScrollerRef={contentScrollerRef}
          appContentRef={appContentRef}
        />
      )}
      <ScrollReset
        id="appRootScrollReset"
        ref={contentScrollerRef}
        style={contentStyles}
        scrollHash={`${pathname}${search}`}
        className={classNames('scroller', appCss.app, {
          [appCss.isLanding]: routeProps.isLanding,
          [appCss.appMiniPlayer]: shouldReserveMiniPlayerSpace,
          [appCss.noHeader]: shouldHideHeader,
        })}
      >
        {!shouldHideHeader && (
          <Header
            showSidebarToggle={showSidebarToggle}
            onSidebarToggle={handleLeftSideToggle}
            isLanding={routeProps.isLanding}
            isMinimalHeader={routeProps.isMinimalHeader}
            minimalHeaderTitle={minimalHeaderTitle}
            unlinkedLogo={routeProps.unlinkedLogo}
            sourceGuideId={guideId}
            isDesktop={isDesktop}
            isLeftSideOpen={isLeftSideOpen}
          />
        )}
        {isRightSideOpen && (
          <RightSide
            hideRightSidebarUpsell={routeProps.hideRightSidebarUpsell}
            matchUrl={routeProps.matchUrl}
            guideId={guideId}
            canShowAds={canShowAds}
            sponsorshipEnabled={sponsorshipEnabled}
          />
        )}
        {!hideLeftSide && (
          <LeftSideLoader
            isLeftSideOpen={isLeftSideOpen}
            isLeftSideDocked={isLeftSideDocked}
            handleLeftSideToggle={handleLeftSideToggle}
            subscriptionEnabled={isSubscriptionEnabled}
            currentGuideId={guideId}
            history={history}
          />
        )}
        {!routeProps.hideLoader && <Loader />}
        <div
          ref={appContentRef}
          className={classNames(appCss.content, {
            ...sidePadding,
            [appCss.isMinimalHeader]: routeProps.isMinimalHeader,
            [appCss.mobile]: isMobile,
            [appCss.isLanding]: routeProps.isLanding,
            [appCss.noHeader]: shouldHideHeader,
          })}
        >
          {content}
        </div>
        {!routeProps.hideFooter && (
          <div className={classNames(sidePadding)}>
            <Footer
              hasRightSide={hasRightSide}
              isLanding={routeProps.isLanding}
              hideLogo={isMinimalFooter}
              hideProductLinks={isMinimalFooter}
              hideSitemap={isMinimalFooter}
              hideSocial={isMinimalFooter}
              hideConsent={isDiscord}
              hideSecurityLink={isDesktop || isDiscord}
              darkMode={isDiscord}
            />
          </div>
        )}
        <GlobalDialogs
          history={history}
          showFloatingGdprButton={showFloatingGdprButton}
          canShowAds={canShowAds}
          sponsorshipEnabled={sponsorshipEnabled}
        />
        <BottomBanner />
      </ScrollReset>
      {showAppToast && (
        <MessagePopover
          className={classNames({
            [appCss.leftSideOffset]: isLeftSideOpen,
            [appCss.rightSideOffset]: isRightSideOpen,
          })}
        />
      )}
    </>
  );
}

AppRoot.propTypes = {
  route: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};
